import * as THREE from "three";
import { useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from "react";

const ArrowheadComponent = forwardRef(({
  params,
  position,
  direction,
  visible,
  arrowHeight = 0.5,
  flip = false,
  scaleFactor = 100,
  distanceScale = 1,
  lineOffset = 5,
  setScaledArrowHeightOut,
  minArrowHeight
}, ref) => {
  const coneRef = useRef();
  const geometryRef = useRef(null);
  const materialRef = useRef(null);
  const lastScaledArrowHeightRef = useRef(null);
  const lastScaledDistanceScaleRef = useRef(null);
  const lastPositionRef = useRef(null);
  const lastDirectionRef = useRef(null);

  // Calculate arrow properties
  const lineLength = useMemo(() => {
    if (!position || !direction) return 0;
    return new THREE.Vector3().subVectors(position, direction).length();
  }, [position, direction]);

  const directionVector = useMemo(() => {
    if (!position || !direction) return new THREE.Vector3(0, 1, 0);
    return new THREE.Vector3().subVectors(direction, position).normalize();
  }, [position, direction]);

  const scaledHeight = useMemo(() => {
    const height = THREE.MathUtils.clamp(
      (arrowHeight * lineOffset * distanceScale / scaleFactor), 
      minArrowHeight / scaleFactor, 
      lineLength * 0.4
    );
    return height;
  }, [arrowHeight, lineOffset, distanceScale, scaleFactor, minArrowHeight, lineLength]);

  const scaledDistanceScale = useMemo(() => {
    return THREE.MathUtils.clamp(distanceScale / scaleFactor, 0, scaledHeight / 4);
  }, [distanceScale, scaleFactor, scaledHeight]);

  // Update scaledArrowHeight output when it changes
  useEffect(() => {
    if (lastScaledArrowHeightRef.current !== scaledHeight && setScaledArrowHeightOut) {
      setScaledArrowHeightOut(scaledHeight);
      lastScaledArrowHeightRef.current = scaledHeight;
    }
  }, [scaledHeight, setScaledArrowHeightOut]);

  // Update arrow position and orientation
  useEffect(() => {
    if (!coneRef.current || !position || !direction) return;

    const lineOffSetTotal = THREE.MathUtils.clamp(
      (lineOffset / scaleFactor) + scaledHeight, 
      minArrowHeight / scaleFactor, 
      lineLength * 0.25
    );

    let arrowDirection;
    let arrowPosition;

    if (scaledHeight > (0.25 * lineLength)) {
      // Point inwards for short lines
      arrowDirection = directionVector.clone();
      arrowPosition = new THREE.Vector3().copy(position)
        .sub(directionVector.clone().multiplyScalar(lineOffSetTotal))
        .add(directionVector.clone().multiplyScalar(scaledHeight / 2));
    } else {
      // Point outwards for normal lines
      arrowDirection = directionVector.clone().negate();
      arrowPosition = new THREE.Vector3().copy(position)
        .add(directionVector.clone().multiplyScalar(lineOffSetTotal))
        .sub(directionVector.clone().multiplyScalar(scaledHeight / 2));
    }

    // Only update if position or direction actually changed
    const positionChanged = !lastPositionRef.current || 
      !lastPositionRef.current.equals(arrowPosition);
    const directionChanged = !lastDirectionRef.current || 
      !lastDirectionRef.current.equals(arrowDirection);

    if (positionChanged || directionChanged) {
      coneRef.current.quaternion.setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        arrowDirection
      );
      coneRef.current.position.copy(arrowPosition);
      lastPositionRef.current = arrowPosition.clone();
      lastDirectionRef.current = arrowDirection.clone();
    }
  }, [position, direction, scaledHeight, lineOffset, scaleFactor, minArrowHeight, lineLength, directionVector]);

  // Update geometry when dimensions change
  useEffect(() => {
    if (!coneRef.current) return;

    const needsGeometryUpdate = 
      lastScaledDistanceScaleRef.current !== scaledDistanceScale ||
      lastScaledArrowHeightRef.current !== scaledHeight;

    if (needsGeometryUpdate) {
      // Dispose old geometry if it exists
      if (geometryRef.current) {
        geometryRef.current.dispose();
      }

      // Create new geometry with updated dimensions
      geometryRef.current = new THREE.ConeGeometry(scaledDistanceScale, scaledHeight, 8);
      
      // Update the mesh geometry
      if (coneRef.current.geometry) {
        coneRef.current.geometry.dispose();
      }
      coneRef.current.geometry = geometryRef.current;

      lastScaledDistanceScaleRef.current = scaledDistanceScale;
      lastScaledArrowHeightRef.current = scaledHeight;
    }
  }, [scaledDistanceScale, scaledHeight]);

  // Initialize material once
  useEffect(() => {
    if (!coneRef.current) return;

    if (!materialRef.current) {
      materialRef.current = new THREE.MeshBasicMaterial({
        color: params.line_colour,
        transparent: false,
        opacity: 1,
      });
      if (coneRef.current.material) {
        coneRef.current.material.dispose();
      }
      coneRef.current.material = materialRef.current;
    } else {
      // Update material color if it changed
      if (materialRef.current.color.getHex() !== new THREE.Color(params.line_colour).getHex()) {
        materialRef.current.color.set(params.line_colour);
      }
    }
  }, [params.line_colour]);

  // Expose the mesh directly for direct Three.js manipulation
  useImperativeHandle(ref, () => coneRef.current, []);

  return (
    <mesh ref={coneRef} visible={visible} />
  );
});

ArrowheadComponent.displayName = 'Arrowhead';
export const Arrowhead = ArrowheadComponent;